import {
  workListSchema,
  workSessionSchema,
  type RecordWorkRequest,
  type WorkList,
  type WorkSessionResponse,
} from '@gamble-chess/contracts';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { requestJson, requestNoContent } from './client.ts';
import { queryKeys } from './queryKeys.ts';

export function useWork(): UseQueryResult<WorkList> {
  return useQuery({
    queryKey: queryKeys.work,
    queryFn: ({ signal }) =>
      requestJson('/api/work-sessions', workListSchema, 'Loading your work failed', { signal }),
  });
}

/** Everything about work changes a balance, so the ledger is refreshed with it. */
function useWorkMutation<TInput, TResult>(
  send: (input: TInput) => Promise<TResult>,
): UseMutationResult<TResult, Error, TInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: send,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.work }),
        queryClient.invalidateQueries({ queryKey: queryKeys.ledger }),
      ]);
    },
  });
}

export function useRecordWork(): UseMutationResult<WorkSessionResponse, Error, RecordWorkRequest> {
  return useWorkMutation((request: RecordWorkRequest) =>
    requestJson(
      '/api/work-sessions',
      workSessionSchema,
      'Recording your work failed. Please try again.',
      { method: 'POST', body: request },
    ),
  );
}

export function useStopWork(): UseMutationResult<WorkSessionResponse, Error, string> {
  return useWorkMutation((id: string) =>
    requestJson(
      `/api/work-sessions/${encodeURIComponent(id)}/stop`,
      workSessionSchema,
      'Stopping the timer failed. Please try again.',
      { method: 'POST' },
    ),
  );
}

export function useDiscardWork(): UseMutationResult<void, Error, string> {
  return useWorkMutation((id: string) =>
    requestNoContent(
      `/api/work-sessions/${encodeURIComponent(id)}`,
      'Discarding the timer failed. Please try again.',
      { method: 'DELETE' },
    ),
  );
}
